import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import XIcon from '@mui/icons-material/X';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageSelector from '@/ui-kit/LanguageSelector';
import clsx from 'clsx';
import { useLocation, Link } from 'react-router-dom';
import { useAppSelector } from '@/app/hooks';
import { selectPublicSettings } from '@/features/publicSettings/publicSettingsSlice';

interface Props {
  authRoutes: boolean;
}

export const Footer: React.FC<Props> = ({ authRoutes }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const publicSettingsState = useAppSelector(selectPublicSettings);
  const publicSettings = publicSettingsState.settings;
  const currentYear = new Date().getFullYear();

  const whiteBackground = () => {
    return ['/faq', '/forgot-password', '/phone-verification', '/success-login', '/create-new-password'].includes(location.pathname);
  };

  const isAuthDark = authRoutes && !whiteBackground();

  const getBankPhoneNumber = () =>
    publicSettings.contactInfo?.bank_phone_number?.[0] || process.env.REACT_APP_BANK_PHONE_NUMBER || '+237 222 23 30 68';

  const getWhatsAppNumber = () =>
    publicSettings.contactInfo?.whatsapp_number?.[0] || process.env.REACT_APP_BANK_WHATSAPP_NUMBER || '+211 922 442 447';

  const getAddress = () => publicSettings.contactInfo?.address?.[0] || '';

  const getSocialLinks = () => ({
    facebook: publicSettings.contactInfo?.facebook?.[0] || '',
    x: publicSettings.contactInfo?.x?.[0] || '',
    linkedin: publicSettings.contactInfo?.linkedin?.[0] || '',
    instagram: publicSettings.contactInfo?.instagram?.[0] || '',
    youtube: publicSettings.contactInfo?.youtube?.[0] || '',
  });

  const getAboutUrl = () => publicSettings.bankInfo?.aboutUrl || null;

  const socialLinks = getSocialLinks();
  const address = getAddress();
  const aboutUrl = getAboutUrl();
  const bankPhone = getBankPhoneNumber();
  const whatsappPhone = getWhatsAppNumber();

  return (
    <footer className={clsx(styles.footer, isAuthDark && styles['footer--dark'])}>
      {/* LEFT — contact + links + social */}
      <div className={styles.footer__left}>
        <div className={styles.footer__item}>
          <CallOutlinedIcon className={styles.footer__icon} />
          <span>{bankPhone}</span>
        </div>

        <span className={styles.footer__sep} />

        <div className={styles.footer__item}>
          <WhatsAppIcon className={styles.footer__icon} />
          <span>{whatsappPhone}</span>
        </div>

        {address && (
          <>
            <span className={styles.footer__sep} />
            <div className={styles.footer__item}>
              <LocationOnIcon className={styles.footer__icon} />
              <span>{address}</span>
            </div>
          </>
        )}
      </div>

      {/* RIGHT — faq + about + social + copyright + language */}
      <div className={clsx(styles.footer__right, (!authRoutes || whiteBackground()) && styles['footer__right--static'])}>
        <Link to='/faq' className={styles.footer__link}>
          {t('footer.faq')}
        </Link>

        <span className={styles.footer__sep} />

        <a href={aboutUrl || '/about-us'} className={styles.footer__link} target='_blank' rel='noopener noreferrer'>
          {t('footer.aboutUs')}
        </a>

        {Object.values(socialLinks).some(Boolean) && (
          <>
            <span className={styles.footer__sep} />
            <div className={styles.footer__social}>
              {socialLinks.facebook && (
                <a
                  href={socialLinks.facebook}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label='Facebook'
                  className={styles.footer__socialLink}
                >
                  <FacebookIcon className={styles.footer__icon} />
                </a>
              )}
              {socialLinks.x && (
                <a href={socialLinks.x} target='_blank' rel='noopener noreferrer' aria-label='X' className={styles.footer__socialLink}>
                  <XIcon className={styles.footer__icon} />
                </a>
              )}
              {socialLinks.linkedin && (
                <a
                  href={socialLinks.linkedin}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label='LinkedIn'
                  className={styles.footer__socialLink}
                >
                  <LinkedInIcon className={styles.footer__icon} />
                </a>
              )}
              {socialLinks.instagram && (
                <a
                  href={socialLinks.instagram}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label='Instagram'
                  className={styles.footer__socialLink}
                >
                  <InstagramIcon className={styles.footer__icon} />
                </a>
              )}
              {socialLinks.youtube && (
                <a
                  href={socialLinks.youtube}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label='YouTube'
                  className={styles.footer__socialLink}
                >
                  <YouTubeIcon className={styles.footer__icon} />
                </a>
              )}
            </div>
          </>
        )}

        <span className={styles.footer__sep} />

        <span className={styles.footer__copy}>
          © {currentYear} {t('footer.afrilandBank')}
          {process.env.REACT_APP_VERSION_CODE && <span className={styles.footer__version}> · {process.env.REACT_APP_VERSION_CODE}</span>}
        </span>

        {(!authRoutes || whiteBackground()) && <LanguageSelector />}
      </div>
    </footer>
  );
};
